'use client';

import { createContext, useContext, useState, useEffect, type ReactNode, useCallback } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

interface NetworkContextType {
  network: string;
  setNetwork: (network: string) => void;
  availableNetworks: string[]; // Or a more specific type if you have defined network names
}

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

const DEFAULT_NETWORK = 'mainnet'; // Define your default network
const AVAILABLE_NETWORKS = ['mainnet', 'testnet', 'regtest']; // Example networks

export const NetworkProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [network, setCurrentNetwork] = useState<string>(() => {
    const networkFromUrl = searchParams.get('network');
    return AVAILABLE_NETWORKS.includes(networkFromUrl || '') ? networkFromUrl! : DEFAULT_NETWORK;
  });

  const setNetwork = useCallback(
    (newNetwork: string) => {
      if (AVAILABLE_NETWORKS.includes(newNetwork)) {
        setCurrentNetwork(newNetwork);
        const current = new URLSearchParams(Array.from(searchParams.entries()));
        current.set('network', newNetwork);
        const search = current.toString();
        const query = search ? `?${search}` : '';
        router.push(`${pathname}${query}`);
      } else {
        console.warn(`Attempted to set an invalid network: ${newNetwork}`);
        // Optionally, set to default or handle error
        setCurrentNetwork(DEFAULT_NETWORK);
        const current = new URLSearchParams(Array.from(searchParams.entries()));
        current.set('network', DEFAULT_NETWORK);
        const search = current.toString();
        const query = search ? `?${search}` : '';
        router.push(`${pathname}${query}`);
      }
    },
    [pathname, router, searchParams]
  );

  // Effect to initialize network from URL on mount and update URL if network changes from other sources
  useEffect(() => {
    const networkFromUrl = searchParams.get('network');
    if (
      networkFromUrl &&
      AVAILABLE_NETWORKS.includes(networkFromUrl) &&
      networkFromUrl !== network
    ) {
      setCurrentNetwork(networkFromUrl);
    } else if (!networkFromUrl || !AVAILABLE_NETWORKS.includes(networkFromUrl)) {
      // If URL has no network or an invalid one, update URL with current state (which is default or last valid)
      const current = new URLSearchParams(Array.from(searchParams.entries()));
      current.set('network', network); // network here is the state, already defaulted
      const search = current.toString();
      const query = search ? `?${search}` : '';
      // Use replace to avoid adding to history if it's just correcting/setting default on load
      router.replace(`${pathname}${query}`);
    }
  }, [searchParams, router, pathname, network]);

  return (
    <NetworkContext.Provider value={{ network, setNetwork, availableNetworks: AVAILABLE_NETWORKS }}>
      {children}
    </NetworkContext.Provider>
  );
};

export const useNetwork = (): NetworkContextType => {
  const context = useContext(NetworkContext);
  if (context === undefined) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
};
